import { UseFormReturn } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import SysColumn from "app/class/sys-column";
import SysRecord from "app/class/record";
import { fetchTranslationResolve } from "app/hooks/useTranslationResolver";
import { DisplayValueDeps, resolveDisplayValue } from "./clientScriptDisplayValue";
import {
    ClientActionBaseContext,
    executeClientAction,
} from "modules/record/shared/services/clientActionRuntime";
import { FieldRuntimeProps } from "../types/record-producer-types";

export type ClientScriptEventType = "onLoad" | "onSubmit" | "onChange";

export interface ClientScriptEvent {
    type: ClientScriptEventType;
    field?: string;
    oldValue?: unknown;
    newValue?: unknown;
}

export interface ClientScriptFormApi {
    getValue: (fieldName: string) => unknown;
    getDisplayValue: (fieldName: string, language?: string) => Promise<unknown>;
    setValue: (fieldName: string, value: unknown) => void;
    setVisible: (fieldName: string, visible: boolean) => void;
    setRequired: (fieldName: string, required: boolean) => void;
    setReadonly: (fieldName: string, readonly: boolean) => void;
    addError: (message: string) => void;
    addInfo: (message: string) => void;
    addFieldError: (fieldName: string, message: string) => void;
    clearFieldError: (fieldName: string) => void;
    addFieldInfo: (fieldName: string, message: string) => void;
    clearFieldInfo: (fieldName: string) => void;
}

export interface ExecuteClientScriptInput {
    script: SysRecord;
    event: ClientScriptEvent;
    form: UseFormReturn<any>;
    baseContext: ClientActionBaseContext;
    current: Record<string, unknown>;
    previous?: Record<string, unknown> | null;
    table: Record<string, unknown>;
    tableName: string;
    columns: SysColumn[];
    currentLanguage: string;
    resolveTranslations?: typeof fetchTranslationResolve;
    translateMessage: (message: string) => string;
    setFieldRuntimeState: (
        fieldName: string,
        patch: FieldRuntimeProps | ((previous: FieldRuntimeProps) => FieldRuntimeProps)
    ) => void;
}

export interface ExecuteClientScriptResult {
    blocked: boolean;
}

export const executeClientScript = async ({
    script,
    event,
    form,
    baseContext,
    current,
    previous = null,
    table,
    tableName,
    columns,
    currentLanguage,
    resolveTranslations = fetchTranslationResolve,
    translateMessage,
    setFieldRuntimeState,
}: ExecuteClientScriptInput): Promise<ExecuteClientScriptResult> => {
    const fieldNames = new Set(columns.map((column) => column.name));
    const formApi = createClientScriptFormApi({
        form,
        baseContext,
        fieldNames,
        columns,
        currentLanguage,
        resolveTranslations,
        translateMessage,
        setFieldRuntimeState,
    });

    const result = await executeClientAction({
        script: String(script.script || ""),
        mode: "form",
        baseContext,
        current,
        table,
        tableName,
        timeoutMs: script.timeout_ms,
        extraContext: {
            previous: previous || {},
            event,
            form: formApi,
        },
    });

    return {
        blocked: result.returnValue === false,
    };
};

export const createClientScriptFormApi = ({
    form,
    baseContext,
    fieldNames,
    columns,
    currentLanguage,
    resolveTranslations = fetchTranslationResolve,
    displayValueDeps,
    translateMessage,
    setFieldRuntimeState,
}: {
    form: UseFormReturn<any>;
    baseContext: ClientActionBaseContext;
    fieldNames: Set<string>;
    columns: SysColumn[];
    currentLanguage: string;
    resolveTranslations?: typeof fetchTranslationResolve;
    displayValueDeps?: Partial<DisplayValueDeps>;
    translateMessage: ExecuteClientScriptInput["translateMessage"];
    setFieldRuntimeState: ExecuteClientScriptInput["setFieldRuntimeState"];
}): ClientScriptFormApi => ({
    getValue: (fieldName: string) => form.getValues(fieldName),
    getDisplayValue: async (fieldName: string, language?: string) => {
        if (!fieldNames.has(fieldName)) return undefined;

        const value = form.getValues(fieldName);
        const column = columns.find((candidate) => candidate.name === fieldName);
        return resolveDisplayValue({
            column,
            value,
            language: language?.trim() || currentLanguage,
            deps: { ...displayValueDeps, resolveTranslations: displayValueDeps?.resolveTranslations ?? resolveTranslations },
        });
    },
    setValue: (fieldName: string, value: unknown) => {
        if (!fieldNames.has(fieldName)) return;

        form.setValue(fieldName, value, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
    },
    setVisible: (fieldName: string, visible: boolean) => {
        if (!fieldNames.has(fieldName)) return;

        setFieldRuntimeState(fieldName, (previous) => ({
            ...previous,
            hidden: !visible,
        }));
    },
    setRequired: (fieldName: string, required: boolean) => {
        if (!fieldNames.has(fieldName)) return;

        setFieldRuntimeState(fieldName, (previous) => ({
            ...previous,
            required,
        }));
    },
    setReadonly: (fieldName: string, readonly: boolean) => {
        if (!fieldNames.has(fieldName)) return;

        setFieldRuntimeState(fieldName, (previous) => ({
            ...previous,
            readonly,
        }));
    },
    addError: (message: string) => {
        baseContext.alert("CLIENT_SCRIPT_ERROR", translateMessage(message));
    },
    addInfo: (message: string) => {
        baseContext.info("CLIENT_SCRIPT_INFO", translateMessage(message));
    },
    addFieldError: (fieldName: string, message: string) => {
        if (!fieldNames.has(fieldName)) return;

        form.setError(fieldName, {
            type: "clientScript",
            message: translateMessage(message),
        });
    },
    clearFieldError: (fieldName: string) => {
        if (!fieldNames.has(fieldName)) return;

        form.clearErrors(fieldName);
    },
    addFieldInfo: (fieldName: string, message: string) => {
        if (!fieldNames.has(fieldName)) return;

        setFieldRuntimeState(fieldName, (previous) => ({
            ...previous,
            helperText: translateMessage(message),
        }));
    },
    clearFieldInfo: (fieldName: string) => {
        if (!fieldNames.has(fieldName)) return;

        setFieldRuntimeState(fieldName, (previous) => ({
            ...previous,
            helperText: undefined,
        }));
    },
});

export const buildClientScriptBaseContext = ({
    notifyApi,
    navigate,
    userSession,
}: {
    notifyApi: {
        alert: (key: string, message: string) => void;
        warning: (key: string, message: string) => void;
        success: (key: string, message: string) => void;
        info: (key: string, message: string) => void;
    };
    navigate: NavigateFunction;
    userSession?: unknown;
}): ClientActionBaseContext => ({
    ...notifyApi,
    currentUser: userSession,
    navigate,
});
